import { toast } from 'react-toastify';

import { dispatchDisappearMessage } from '../../../Actions/DisappearActions';
import SDK from '../../../Components/SDK';
import { isMessageExpiredLocally } from '../../../Helpers/Chat/DisappearScheduler';
import logger from '../../../Helpers/logger';
import { blockOfflineAction, getUserIdFromJid } from '../../../Helpers/Utility';
import { i18n } from '../../../localization/i18n';
import { T, TOAST } from '../../../localization/toastKeys';
import { applyKeepChange, fetchKeptMessages, selectKeptList } from '../../../slice/keptMessagesSlice';
import { isExpiredOnUnkeep } from './keepRules';

const UNKEEP_OK = 'ok';
const ALREADY_KEPT = 'already_kept';

const okItemsOf = response => (response?.data?.items || []).filter(item => item.result === UNKEEP_OK);

// A message unkept after its timer ran out leaves the thread at once.
const expireUnkeptMessages = (state, chatId, items) => {
    const loadedMessages = state.chatConversationHistory?.data?.[chatId]?.messages || {};
    items.forEach(({ msgId, isExpired }) => {
        if (isMessageExpiredLocally(msgId)) return;
        const message = loadedMessages[msgId];
        if (!isExpiredOnUnkeep(message, isExpired)) return;
        dispatchDisappearMessage(message || { msgId }, chatId);
    });
};

/**
 * Keeps one message or unkeeps several, then refreshes the Kept Messages list.
 *
 * @param {string} chatJid Conversation JID: the peer for 1-1, the group JID for a group.
 * @param {string[]} msgIds Message ids; keep takes exactly one.
 * @param {boolean} isKeep True to keep, false to unkeep.
 * @returns {Promise<boolean>} True when the server applied the change.
 */
export const toggleKeep = (chatJid, msgIds, isKeep) => async (dispatch, getState) => {
    if (!chatJid || !msgIds?.length || blockOfflineAction(i18n.t('common.INTERNET_ERROR_MESSAGE'))) return false;
    const chatId = getUserIdFromJid(chatJid);
    const action = i18n.t(isKeep ? 'message.ACTION_KEEP' : 'message.ACTION_UNKEEP');

    const response = await SDK.updateKeepStatusMessage(chatJid, isKeep ? msgIds[0] : msgIds, isKeep);
    const appliedIds = isKeep ? msgIds.slice(0, 1) : okItemsOf(response).map(item => item.msgId);

    if (response?.statusCode !== 200 || !appliedIds.length) {
        // Someone else kept it first: show their keep instead of a stale Keep action.
        if (response?.reason === ALREADY_KEPT) {
            dispatch(
                applyKeepChange({
                    chatId,
                    isKept: true,
                    keptBy: response.keptBy,
                    keptAt: response.keptAt,
                    msgIds: msgIds.slice(0, 1),
                }),
            );
        }
        logger.error('Keep message failed', { isKeep, count: msgIds.length, reason: response?.reason });
        toast.error(i18n.t(`${TOAST}.${T.ACTION_FAILED_RETRY}`, { action }));
        return false;
    }

    const { keptBy, keptAt } = response.data || {};
    dispatch(applyKeepChange({ chatId, isKept: isKeep, keptBy, keptAt, msgIds: appliedIds }));
    if (!isKeep) expireUnkeptMessages(getState(), chatId, okItemsOf(response));
    dispatch(fetchKeptMessages(chatJid));
    toast.success(i18n.t(`${TOAST}.${T.ACTION_SUCCESSFUL}`, { action }));
    return true;
};

/**
 * Applies a keep push: from another participant, or the user's own other device.
 *
 * @param {{fromUserJid: string, fromUserId: string, keptStatus: number, keptBy: string,
 *   keptAt: number, items: {msgId: string, isExpired: boolean}[]}} push Keep push.
 */
export const receiveKeepPush = push => (dispatch, getState) => {
    const items = push?.items || [];
    const chatId = push?.fromUserId;
    if (!chatId || !items.length) return;
    const isKept = Number(push.keptStatus) === 1;

    dispatch(
        applyKeepChange({
            chatId,
            isKept,
            keptBy: push.keptBy,
            keptAt: push.keptAt,
            msgIds: items.map(item => item.msgId),
        }),
    );
    if (!isKept) expireUnkeptMessages(getState(), chatId, items);
    if (selectKeptList(getState(), chatId).isLoaded) dispatch(fetchKeptMessages(push.fromUserJid));
};
